import type { Config } from '@gmn-portal/backend';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { payloadClient } from '@/core/api';

type User = Config['collections']['users'];

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (data: User) => void;
  initialized: boolean;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => {},
  logout: async () => {},
  updateUser: async () => {},
  initialized: false,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!user;

  const hydrateUserGroup = async (nextUser: User | null): Promise<User | null> => {
    if (!nextUser) return null;
    const groupValue = nextUser.userGroup;
    const hasGroup =
      groupValue &&
      typeof groupValue === 'object' &&
      'permissions' in groupValue &&
      'scopes' in groupValue;
    if (hasGroup) return nextUser;

    const groupId =
      typeof groupValue === 'string'
        ? groupValue
        : typeof groupValue === 'object' && groupValue && 'id' in groupValue
          ? String((groupValue as { id?: string }).id ?? '')
          : '';

    if (!groupId) return nextUser;

    try {
      const group = await payloadClient.findById({
        collection: 'user-groups',
        id: groupId,
        depth: 0,
      });
      return { ...nextUser, userGroup: group };
    } catch {
      return nextUser;
    }
  };

  useEffect(() => {
    async function initializeAuth() {
      try {
        const token = await payloadClient.getToken();
        if (token) {
          const meData = await payloadClient.me();
          if (meData) {
            const hydrated = await hydrateUserGroup(meData.user);
            setUser(hydrated);
          }
        }
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    }

    initializeAuth();
  }, []);

  const login = async ({ email, password }: { email: string; password: string }) => {
    setLoading(true);
    try {
      const data = await payloadClient.login({ email, password });
      const hydrated = await hydrateUserGroup(data.user);
      setUser(hydrated);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await payloadClient.logout();
    setUser(null);
  };

  const updateUser = (data: User) => {
    void (async () => {
      const needsHydration = !data.userGroup || typeof data.userGroup === 'string';
      const hydrated = needsHydration ? await hydrateUserGroup(data) : data;
      setUser((prev) => {
        if (!hydrated) return hydrated;
        const nextGroup = hydrated.userGroup;
        if (nextGroup && typeof nextGroup !== 'string') {
          return hydrated;
        }
        const prevGroup = prev?.userGroup;
        if (prevGroup && typeof prevGroup !== 'string') {
          return { ...hydrated, userGroup: prevGroup };
        }
        return hydrated;
      });
    })();
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, login, logout, updateUser, initialized, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
